import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { login, logout, checkAuth } from '@/shared/apis/auth'
import { isCertDomain } from '@/shared/constants/const'
import {
  clearUserScopedStorage,
  localStorageWrapper,
} from '@/shared/helpers/localStorage'
import { queryKeys } from './queryKeys'
import axios from 'axios'

// ========================================
// Types
// ========================================

interface EditMode {
  kana: boolean
  insurance_num: boolean
  birthday: boolean
  allowed_insurance_num_blank: boolean
}

interface User {
  id: string
  pharmacy_name: string
  plan: string
  qr_size: string
  edit_mode: EditMode
  app_type: string
  is_annotator: boolean
  is_activated_photo: boolean
  show_save_csv_button: boolean
  is_image_rotatable: boolean
  editable_item_font_size: string
  is_audio_upload: boolean
  is_use_nsips: boolean
}

interface CheckAuthResponse {
  version: null
  user: User
}

interface LoginVariables {
  email: string
  password: string
}

interface LoginResult {
  user: User
}

interface LoginError {
  type:
    | 'network'
    | 'invalid_credentials'
    | 'account_locked'
    | 'wrong_environment'
    // login は 200 なのにセッションが保持されなかった。ブラウザが cookie を落としている
    | 'embedded_session_blocked'
    // 5xx。利用者側にできることが無く、待つ以外の案内をしてはいけない
    | 'server_error'
    | 'unknown'
  message: string
  remainingAttempts?: number
  cooloffTime?: number
}

/**
 * 401 からセッションを取り戻す手段。
 *
 * shared 層は「なぜ cookie が落ちたのか」も「どうやって取り戻すのか」も知らない。
 * 実体は feature 側が持ち、呼び出し側 (App.tsx / pages/login.tsx) が注入する。
 * 唯一の実装は `@/features/anycompass` の `embedSessionRecovery` (Storage Access API)。
 *
 * 渡さなければ従来どおりの挙動 = 401 は素直に /login へ退避する。
 */
export interface SessionRecovery {
  /**
   * 復帰を試す価値がある文脈か。
   *
   * **同期で返すこと。** 認証は iframe 起動と同じ秒に走るため、ハンドシェイクの完了を
   * 待つような非同期判定では窓を素通りする (log/188 work-log/6 §3.1)。
   */
  canRecover: () => boolean
  /**
   * 復帰を試みる。true = 取り戻せた。
   *
   * ユーザー操作のハンドラから同期的に呼ばれる (transient user activation を使うため、
   * 呼ぶ側は手前に await を挟まない)。
   */
  recover: () => Promise<boolean>
}

// ========================================
// LoginError の組み立て
// ========================================

// 文言の共通方針（読むのは薬局の事務スタッフで、エンジニアではない）:
//
// 1. 「何が起きたか」を 1 文で断定し、「何をすればよいか」を 1 文で書く。それ以上足さない
// 2. 用語を持ち込まない。セッション / Cookie / 認証情報 / URL / 環境 は使わない
// 3. **利用者自身にできない操作を指示しない。** できないことは「サポート窓口にご連絡ください」に寄せる。
//    連絡先の呼び方は「サポート窓口」で統一する (「担当者」「担当の方」を混在させない)
// 4. 再試行を促してよいのは、時間で直るもの (通信・5xx・ロックアウト) だけ。
//    資格情報の誤りや cookie の問題で「もう一度お試しください」と書くと、実測のように
//    同じ失敗を繰り返させることになる (log/188 work-log/1 §3.3)

// 呼び出し側に渡った後で書き換えられても他へ波及しないよう、共有せず毎回作る
const unknownLoginError = (): LoginError => ({
  type: 'unknown',
  message:
    'ログインできませんでした。時間をおいて、もう一度お試しください。解決しない場合はサポート窓口にご連絡ください。',
})

// 5xx。ここを unknown と分けないと、サーバー障害とブラウザ側の cookie 問題が同じ文言になり、
// 障害中の観測を「設定の影響」と読み違える (log/188 work-log/10 §10.8(1) で実害が出た)
const serverErrorLoginError = (): LoginError => ({
  type: 'server_error',
  message:
    'サーバー側で問題が起きています。少し時間をおいて、もう一度お試しください。',
})

/**
 * axios のエラーを LoginError に写す。400 / 403 / ネットワーク断の分岐はここだけが持つ。
 */
const toLoginError = (e: unknown): LoginError => {
  if (!axios.isAxiosError(e)) return unknownLoginError()

  if (!e.response) {
    // 証明書の期限切れも TLS 層で落ちるのでここに来る。cert ドメインにいる時点で
    // 「ご利用の場合は」と条件を付ける必要は無い
    const certHint =
      isCertDomain && navigator.onLine
        ? '\n改善しない場合は、証明書の期限が切れている可能性があります。サポート窓口にご確認ください。'
        : ''
    return {
      type: 'network',
      message: `サーバーに接続できませんでした。通信状況を確認して、もう一度お試しください。${certHint}`,
    }
  }

  switch (e.response.status) {
    case 400: {
      const remaining_attempts = e.response.data.remaining_attempts
      const cooloff_time = e.response.data.cooloff_time
      // ロックまでの残り回数は back が返したときだけ添える。
      // login の 400 は必ず両方を返すが、パラメータ欠落の 400 は返さないため、
      // 無条件に埋め込むと「あとundefined回」になる。0 以下も同様に出さない
      //（次の試行で 403 のロックアウトに変わるので、警告として意味を持たない）
      const lockoutWarning =
        typeof remaining_attempts === 'number' &&
        remaining_attempts > 0 &&
        typeof cooloff_time === 'number'
          ? `あと${remaining_attempts}回間違えると、${cooloff_time}時間ログインできなくなります。`
          : ''
      return {
        type: 'invalid_credentials',
        // 「正しくない可能性があります」だった。authenticate が拒否した事実は確定しているので断定する。
        // ぼかすと利用者が同じ入力を試し続ける
        message: `メールアドレスまたはパスワードが違います。${lockoutWarning}`,
        remainingAttempts: remaining_attempts,
        cooloffTime: cooloff_time,
      }
    }
    case 403: {
      if (e.response.data.error_type === 'wrong_environment') {
        // unified 環境では backend が cert 提示状態の不整合を error_detail で通知する。
        // error_detail 無し = 旧 split 環境(cert.* / 通常)でのアカウント取り違え。
        // 「クライアント証明書」の「クライアント」は利用者から見て意味を持たない。
        // 実際の操作は端末に出る選択ダイアログで「どれを選ぶか」なので、そこに合わせる
        const errorDetail = e.response.data.error_detail
        let guidance: string
        switch (errorDetail) {
          case 'cert_mismatch':
            guidance =
              '選んだ証明書が、このアカウントのものと違います。証明書を選び直して、もう一度お試しください。'
            break
          case 'cert_required':
            guidance =
              'このアカウントは証明書が必要です。証明書を選んで、もう一度お試しください。'
            break
          case 'cert_not_allowed':
            guidance =
              'このアカウントでは証明書を使いません。証明書を選ばずに、もう一度お試しください。'
            break
          default:
            // 旧 split 環境 (cert.* と通常で URL が分かれていた頃) のアカウント取り違え。
            // 「通常環境の URL から」では利用者が何を開けばよいか分からないので、
            // 手元にある 2 つのブックマークのどちらかを指せる言い方にする
            guidance = isCertDomain
              ? 'このアカウントは、証明書を使わない方のログイン画面からご利用ください。'
              : 'このアカウントは、証明書を使う方のログイン画面からご利用ください。'
        }
        return { type: 'wrong_environment', message: guidance }
      }
      return {
        type: 'account_locked',
        // 一番知りたいのは「待てば入れるのか」。時間で自動的に解ける性質をまず伝える。
        // 解除時間は back が 403 の本文に載せないため、具体値は書かない
        message:
          'ログインの失敗が続いたため、このアカウントは一時的にロックされています。しばらく時間をおいて、もう一度お試しください。お急ぎの場合はサポート窓口にご連絡ください。',
      }
    }
    default:
      if (e.response.status >= 500) return serverErrorLoginError()
      return unknownLoginError()
  }
}

/**
 * login は 200 なのに直後の check_auth が 401 = セッション cookie が保存されなかった。
 *
 * ブラウザ差の問題であり、資格情報も証明書も正しい。「予期しないエラーが発生しました。
 * もう一度お試しください」に落として再試行を促してはならない (実測で 1 端末が 1 時間に
 * 10 回ログインを試行していた。log/188 work-log/1 §3.3)。
 *
 * UA は見ない。「login 200 の直後に check_auth 401」という観測事実そのもので分岐するので、
 * 将来 WebKit が CHIPS を実装すれば自動的にこの経路に入らなくなる。
 *
 * 復帰できる文脈で出す文言は、**構成を決め打ちせず条件付きで案内する**。
 * `isRecoverableContext` は「埋め込みの中で requestStorageAccess がある」でしかなく、
 * requestStorageAccess は Chromium にもあるため **PC の Edge もここに入りうる**
 * (Cookie を全ブロックする設定などで 401 になった場合)。
 * 「Safari で開き直せ」と断定すると、その PC には存在しない操作を案内することになる。
 *
 * 実機で分かっているのは、iPad の Safari のタブなら完走でき (log/188 work-log/10 §10)、
 * 到達できない既知の構成は「ホーム画面に追加した Web アプリ」だけ、という 1 点
 * (同 §5。Safari と cookie の保存領域が別で storage access を取れない)。
 * そこで**その構成に該当する人にだけ刺さる条件文**にしてある。該当しない環境の人は
 * 「自分は違う」と読み飛ばせるので誤誘導にならない。
 *
 * standalone (ホーム画面 Web アプリ) を検出して分岐する案は採らなかった。
 * `display-mode` / `navigator.standalone` が **iframe の中で親の表示モードを返すか**が
 * 未検証で、未検証の判定で文面を切り替えると誤誘導の形が変わるだけになるため
 * (log/188 work-log/10 §11.4)。
 *
 * @param isRecoverableContext 復帰手段が生きている文脈か (= 埋め込みの中か)。
 *   注入された SessionRecovery から取る。ここでは「埋め込み」という語を知らない
 */
const embeddedSessionBlockedError = (
  isRecoverableContext: boolean
): LoginError => ({
  type: 'embedded_session_blocked',
  message: isRecoverableContext
    ? // 「薬歴画面を開き直す」とは書かない。利用者が見ているのは埋め込みの中の Kakeru で、
      // その枠だけを開き直す操作は存在しない。開き直すのは画面全体なので目的語を置かない
      'ログインはできましたが、この開き方ではログインした状態を保てません。ホーム画面のアイコンから開いている場合は、いったん閉じて Safari から開き直してください。'
    : // 埋め込みの外で cookie が保存できない = ブラウザ側の設定や拡張が原因。
      // 「Cookie 設定をご確認ください」は用語であり、事務スタッフが辿れる操作でもない。
      // 自分で直せないものは人に振らせる (方針 3)
      'ログインはできましたが、お使いのブラウザがログイン情報を保存できない状態になっています。サポート窓口にご連絡ください。',
})

// ========================================
// useLoginMutation
// ========================================

/**
 * ログインMutation（TanStack Query）
 *
 * 責務:
 * - login API + checkAuth API の連続呼び出し
 * - 成功時に localStorage に userId, IsAudioUpload, IsUseNsips を設定
 * - エラーハンドリング（400, 403, ネットワーク）
 *
 * @param recovery 401 の文言を出し分けるためだけに使う。ここでは復帰を試みない
 *   (この時点でパスワードは既に入力されており、救えるのは起動時の 401 の側。
 *   log/188 work-log/6 §6)
 *
 * 使用例:
 * ```tsx
 * const loginMutation = useLoginMutation(embedSessionRecovery)
 * const result = await loginMutation.mutateAsync({ email, password })
 * // result.user を使って dispatch
 * ```
 */
export const useLoginMutation = (recovery?: SessionRecovery) => {
  const queryClient = useQueryClient()

  return useMutation<LoginResult, LoginError, LoginVariables>({
    mutationFn: async ({ email, password }) => {
      // Step 1: ログイン
      // checkAuth と try を分ける。同一 try に入れると checkAuth の 401 が login の
      // エラーとして default (= 予期しないエラー) に落ち、実態と食い違う文言になる
      try {
        await login(email, password)
      } catch (e) {
        throw toLoginError(e)
      }

      try {
        // 前ユーザーの残留データを遮断する (共有端末でのユーザー切替)。
        // logout を経由しない切替 (セッション失効→/login 直行) もここで塞ぐ
        await queryClient.cancelQueries()
        queryClient.clear()
        clearUserScopedStorage()

        // Step 2: 認証情報取得
        const response = await checkAuth()
        const authData = response.data as CheckAuthResponse

        // Step 3: localStorage に保存
        localStorageWrapper.setItem('userId', authData.user.id)
        localStorageWrapper.setItem(
          'IsAudioUpload',
          authData.user.is_audio_upload
        )
        localStorageWrapper.setItem('IsUseNsips', authData.user.is_use_nsips)

        return { user: authData.user }
      } catch (e) {
        // login は 200 で通っている。ここでの 401 は資格情報の誤りではなく、
        // セッション cookie が保存されなかったことを意味する
        if (axios.isAxiosError(e) && e.response?.status === 401) {
          throw embeddedSessionBlockedError(recovery?.canRecover() ?? false)
        }
        throw toLoginError(e)
      }
    },
  })
}

// ========================================
// useCheckAuthQuery
// ========================================

/**
 * 認証状態チェックQuery（TanStack Query）
 *
 * 責務:
 * - checkAuth API でセッションの有効性を確認
 * - 成功時に localStorage に userId, IsAudioUpload, IsUseNsips を設定
 * - 成功時に Redux に isLoggedIn を dispatch
 * - 失敗時に /login へリダイレクト
 * - ただし recovery が「取り戻せる見込みがある」と言う 401 だけは /login へ飛ばさず、
 *   復帰を促すゲートを呼び出し側に出させる
 *
 * 注意:
 * - ログインページ (/login) では認証チェックを実行しない
 *   これにより、ログイン処理とのレース条件を回避する（ADR-054参照）
 * - 起動時の 401 はセッションが切れていなくても iframe を作り直すたびに毎回起きる。
 *   パスワードの再入力を省けるのはこの 1 回目の 401 だけで、login 直後の 2 回目では
 *   もう入力されてしまっている (log/188 work-log/6 §6)
 *
 * @param recovery 復帰手段。**参照が安定していること** (module 定数を渡す)。
 *   毎レンダー別オブジェクトを渡すと recoverSession が作り直される
 *
 * 使用例:
 * ```tsx
 * // App.tsx などで認証チェックを行う
 * const { isRecoverableSessionLoss, recoverSession } =
 *   useCheckAuthQuery(embedSessionRecovery)
 * ```
 */
export const useCheckAuthQuery = (recovery?: SessionRecovery) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  // 復帰を試みて、それでも 401 のままだった状態。以降はゲートを出さず
  // 従来どおり /login へ送る (= ログインフォームと C 案の文言に退避する)
  const [recoveryFailed, setRecoveryFailed] = useState(false)

  // ログイン/ログアウトページ・困ったときガイド・がんの相談チャットでは認証チェックを実行しない
  // (/logout はキャッシュ破棄中に checkAuth が走ると 401 → 二重 navigate になるため。
  //  /guide は「ログインできない」区分を含むガイドなので、未認証でも /login へ送らない。
  //  /counsel は患者が開くページで、URL の token を back が確かめる)
  const isPublicPage =
    location.pathname === '/login' ||
    location.pathname === '/logout' ||
    location.pathname.startsWith('/guide') ||
    location.pathname.startsWith('/counsel/')

  const query = useQuery({
    queryKey: queryKeys.auth.check(),
    queryFn: async () => {
      const response = await checkAuth()
      return response.data as CheckAuthResponse
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !isPublicPage,
  })

  useEffect(() => {
    if (query.isSuccess && query.data) {
      // localStorage に保存
      localStorageWrapper.setItem('userId', query.data.user.id)
      localStorageWrapper.setItem(
        'IsAudioUpload',
        query.data.user.is_audio_upload
      )
      localStorageWrapper.setItem('IsUseNsips', query.data.user.is_use_nsips)
      // Redux に dispatch
      dispatch({ type: 'setIsLoggedIn', isLoggedIn: true })
    }
  }, [query.isSuccess, query.data, dispatch])

  // 起動時の 401 のうち、復帰の見込みがあるものだけを切り出す。
  // **401 を実際に観測したこと**が今 100% 動いている Windows/Edge を守る唯一の条件で、
  // 外すと彼らに不要な導線が出る。Edge も AnyCOMPASS 連携ユーザ = 埋め込みの中にいるため、
  // 文脈の判定 (recovery.canRecover) だけでは彼らを守れない (log/188 work-log/6 §4)。
  // axios 0.21 の isAxiosError は typeof payload === 'object' で素通りするため、
  // error が null (= 正常時) だと中で例外になる。null ガードを先に置く
  const status =
    query.error !== null && axios.isAxiosError(query.error)
      ? query.error.response?.status
      : undefined
  const isUnauthorized = status === 401
  const isRecoverableSessionLoss =
    isUnauthorized && !recoveryFailed && (recovery?.canRecover() ?? false)

  // 403 は資格情報の問題ではない (サーバが「この接続では許可しない」と言っている状態)
  // ので、/login へ送ってログインフォームを出しても何も変わらない。復旧手段は再試行
  // (接続のやり直し) か、別のアカウントでのログインし直しで、ゲート側が提示する。
  // 401 との違い: 401 はセッション cookie の喪失で、storage access で取り戻せる。
  //
  // 一度 403 を観測したらラッチする。再試行 (refetch) 中は query の error が一旦消え、
  // 失敗すると 403 以外のエラー (network 断・5xx) に**置き換わる**ため、status だけを
  // 見るとゲートが瞬断した上で下の effect が navigate('/login') に抜けてしまう
  // (= このゲートの存在理由の否定)。ラッチは成功で解除し、401 へ転じたときは
  // セッション喪失側の分岐に譲る。
  const [accessDeniedLatched, setAccessDeniedLatched] = useState(false)
  // render 中の条件付き set (React 公認の派生調整パターン)。effect にすると
  // ラッチが 1 commit 遅れ、その間にゲートが畳まれて下の navigate が先に走りうる。
  if (status === 403 && !accessDeniedLatched) {
    setAccessDeniedLatched(true)
  } else if (accessDeniedLatched && query.isSuccess) {
    setAccessDeniedLatched(false)
  }
  // 公開ページでは出さない。query は disabled でもキャッシュの error は残るため、
  // ブラウザ Back (SPA 遷移) で /login に戻ったときゲートがログインフォームを
  // 覆ってしまう (ADR-054 が checkAuth を止めている場所でゲートだけ動く形になる)。
  const isAccessDenied =
    !isPublicPage &&
    !isUnauthorized &&
    (status === 403 || (accessDeniedLatched && !query.isSuccess))

  useEffect(() => {
    if (query.isError && !isRecoverableSessionLoss && !isAccessDenied) {
      navigate('/login')
    }
  }, [query.isError, isRecoverableSessionLoss, isAccessDenied, navigate])

  // ゲートの押下ハンドラ。recover() は transient user activation を要求するので、
  // 手前に await を挟まず先頭で呼ぶ。セッション自体は生きている (SESSION_COOKIE_AGE = 90 日)
  // ため、取り戻せれば refetch が 200 になりパスワード入力なしで画面に入れる。
  const { refetch } = query
  const recoverSession = useCallback(async () => {
    if (!recovery || !(await recovery.recover())) {
      setRecoveryFailed(true)
      return
    }
    const result = await refetch()
    if (result.isError) setRecoveryFailed(true)
  }, [recovery, refetch])

  return {
    query,
    isRecoverableSessionLoss,
    isAccessDenied,
    recoverSession,
    // 403 ゲートの「再試行」。refetch が 200 になればゲートは下りる。
    retry: query.refetch,
  }
}

// ========================================
// useLogoutMutation
// ========================================

/**
 * ログアウトMutation（TanStack Query）
 *
 * 責務:
 * - logout API を呼び出し
 * - 完了後（成功/失敗問わず）/login へリダイレクト
 *
 * 使用例:
 * ```tsx
 * const logoutMutation = useLogoutMutation()
 * logoutMutation.mutate()
 * ```
 */
export const useLogoutMutation = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logout,
    onSettled: async () => {
      // 患者名を含むキャッシュ (react-query + localStorage の delta 同期分) を
      // ユーザー境界で必ず破棄する。成功/失敗を問わず実行
      await queryClient.cancelQueries()
      queryClient.clear()
      clearUserScopedStorage()
      navigate('/login')
    },
  })
}
